/**
 * MobileSystemCard Component
 *
 * Card-based display for system data on mobile devices.
 * Replaces the DataTable view for better touch interaction.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ComputerIcon from '@mui/icons-material/Computer';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system/tokens';
import { StatusBadge, ConnectionIndicator } from '@components/design-system';
import { SystemVariantMap } from '@consts/systems';
import { CUT_FEE_MAP, type BillingType } from '@consts/company';
import { AppRoutes } from '@consts/routes';
import type { System } from '@api/system';

interface MobileSystemCardProps {
    system: System;
    nameMap?: {
        locations: Record<number, string>;
        companies: Record<number, string>;
    };
    onClick?: () => void;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                color: colors.neutral[700],
                fontWeight: typographyTokens.fontWeight.medium,
                textAlign: 'right',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
            {value || '-'}
        </Typography>
    </Box>
);

export const MobileSystemCard: React.FC<MobileSystemCardProps> = ({ system, nameMap, onClick }) => {
    const statusMap: Record<string, { label: string; status: 'success' | 'warning' | 'error' }> = {
        Active: { label: 'Active', status: 'success' },
        Inactive: { label: 'Inactive', status: 'warning' },
        Decommissioned: { label: 'Decommissioned', status: 'error' },
    };
    const statusConfig = (system.Status && statusMap[system.Status]) || {
        label: 'Unknown',
        status: 'warning' as const,
    };

    const locationName = system.LocationID && nameMap?.locations?.[system.LocationID];
    const companyName = system.CompanyID && nameMap?.companies?.[system.CompanyID];
    const modelName = system.ProductID ? SystemVariantMap[system.ProductID] : '-';
    const billingPlan =
        system.CutPlanID !== undefined && system.CutPlanID !== null
            ? CUT_FEE_MAP[system.CutPlanID as BillingType] || '-'
            : '-';

    const CardWrapper = onClick ? Box : RouterLink;
    const cardProps = onClick
        ? { onClick, sx: { cursor: 'pointer' } }
        : { to: `${AppRoutes.Organizations}?type=system&id=${system.StationID}`, component: RouterLink };

    return (
        <Box
            component={onClick ? 'div' : RouterLink}
            to={onClick ? undefined : `${AppRoutes.Organizations}?type=system&id=${system.StationID}`}
            onClick={onClick}
            sx={{
                display: 'block',
                textDecoration: 'none',
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
                transition: 'all 150ms ease',
                cursor: onClick ? 'pointer' : undefined,
                '&:hover': {
                    boxShadow: shadows.elevated,
                    borderColor: colors.primary[200],
                },
                '&:active': {
                    transform: 'scale(0.99)',
                },
            }}>
            {/* Header with system info and status */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <ComputerIcon sx={{ color: colors.primary[500], fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                                fontFamily: typographyTokens.fontFamily.mono,
                            }}>
                            {system.SerialNum}
                        </Typography>
                        <StatusBadge label={statusConfig.label} status={statusConfig.status} size='small' />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            mt: 0.25,
                        }}>
                        Station #{system.StationID} • {modelName}
                    </Typography>
                </Box>
            </Box>

            {/* Details */}
            <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
                {locationName && <InfoRow label='Location' value={locationName} />}
                {companyName && <InfoRow label='Company' value={companyName} />}
                <InfoRow label='Billing' value={billingPlan} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}>
                        Connected
                    </Typography>
                    <ConnectionIndicator timestamp={system.LastConnected} />
                </Box>
            </Box>
        </Box>
    );
};

// Loading skeleton for mobile system cards
export const MobileSystemCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='60%' height={24} />
                <Skeleton variant='text' width='40%' height={16} />
            </Box>
        </Box>
        <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
        </Box>
    </Box>
);

export default MobileSystemCard;
